import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import { healthAPI } from '../services/api';
import { cardSx } from '../theme';

const SystemStatusPage = () => {
  const [backendStatus, setBackendStatus] = useState(null);
  const [backendLoading, setBackendLoading] = useState(true);
  const [lastChecked, setLastChecked] = useState(null);
  const [error, setError] = useState(null);

  const checkBackendHealth = async () => {
    setBackendLoading(true);
    setError(null);
    try {
      const response = await healthAPI.checkBackend();
      setBackendStatus({
        healthy: response.data.status === 'healthy',
        message: response.data.message,
      });
      setLastChecked(new Date());
    } catch (err) {
      setBackendStatus({
        healthy: false,
        message: err.response?.data?.error || 'Backend service is unreachable',
      });
      setError('Failed to connect to backend service');
      setLastChecked(new Date());
    } finally {
      setBackendLoading(false);
    }
  };

  useEffect(() => {
    checkBackendHealth();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      checkBackendHealth();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const formatTimestamp = (date) => {
    if (!date) return 'Never';
    return date.toLocaleString();
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">System Status</Typography>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={checkBackendHealth}
          disabled={backendLoading}
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Frontend Status */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ ...cardSx, p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ flexGrow: 1 }}>
                Frontend Service
              </Typography>
              <Chip
                icon={<CheckCircleIcon />}
                label="Online"
                color="success"
                variant="filled"
              />
            </Box>
            <Typography variant="body2" color="textSecondary">
              The frontend application is running and accessible.
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
              Last checked: {formatTimestamp(new Date())}
            </Typography>
          </Paper>
        </Grid>

        {/* Backend Status */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ ...cardSx, p: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ flexGrow: 1 }}>
                Backend Service
              </Typography>
              {backendLoading ? (
                <CircularProgress size={24} />
              ) : backendStatus?.healthy ? (
                <Chip
                  icon={<CheckCircleIcon />}
                  label="Healthy"
                  color="success"
                  variant="filled"
                />
              ) : (
                <Chip
                  icon={<ErrorIcon />}
                  label="Unhealthy"
                  color="error"
                  variant="filled"
                />
              )}
            </Box>
            {backendLoading ? (
              <Typography variant="body2" color="textSecondary">
                Checking backend status...
              </Typography>
            ) : (
              <>
                <Typography variant="body2" color="textSecondary">
                  {backendStatus?.message || 'Status unknown'}
                </Typography>
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                  Last checked: {formatTimestamp(lastChecked)}
                </Typography>
              </>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SystemStatusPage;

